import React, {useEffect, useState} from 'react';
import ProductEditorTemplate from './ProductEditorTemplate';
import productService from '../../services/ProductService';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import cloudStorageFileService from "../../services/CloudStorageFileService";
import authService from "../../services/AuthService";
import axios from "axios";
import {API_URL} from "../../App";
import {getFileUrl} from "../../services/FileService";
import productCategoryService from "../../services/ProductCategoryService";

const ProductEditor = () => {

    const urlParts = window.location.pathname.split('/')
    const id = urlParts[4] === 'create' ? undefined : urlParts[4]

    useTitle([
        {name: 'Raktár', url: '/dashboard/storage'},
        {name: 'Termék', url: '/dashboard/storage/' + (id || 'create')}
    ])

    const [product, setProduct] = useState();
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [waitingForServer, setWaitingForServer] = useState(false)
    const [waitingForCorrection, setWaitingForCorrection] = useState(false)
    const {sendAlertMessage} = useSnackbar()

    const navigate = useNavigate()

    useEffect(() => {
        if (!id) return
        retrieveProduct()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const retrieveProduct = () => {
        if (!id) return
        productService.findById(id)
            .then((response) => setProduct(response))
            .catch(() => sendAlertMessage('Termék lekérése sikertelen', 'error'))

    }

    const [imgUrl, setImgUrl] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);
    useEffect(() => {
        if (!product?.picture?.id) return;
        setLoadingImage(true);
        let url;
        getFileUrl('', `/cloud-storage-files/${product.picture.id}`).then(u => {
            url = u;
            setImgUrl(u);
            setLoadingImage(false);
        });

        return () => {
            if (url) window.URL.revokeObjectURL(url); // cleanup
        };
    }, [product?.picture?.id]);

    const [productCategories, setProductCategories] = useState([]);
    useEffect(() => {
        productCategoryService.findAll()
            .then(response => setProductCategories(response))
            .catch(error => console.error(error))
    }, []);

    const onChange = (event) => {
        const {name, value} = event.target;
        setProduct((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const onChangeCheckbox = (event) => {
        const {checked, name} = event.target
        setProduct((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const onChangeSelectItem = (newValue, field) => {
        setProduct((prev) => ({
            ...prev,
            [field]: newValue,
        }));
    };

    const onChangeProductItem = (event, index) => {
        const {name, value} = event.target;

        setProduct(prev => {
            const updatedItems = [...prev.productItems];
            updatedItems[index] = {
                ...updatedItems[index],
                [name]: value,
            };

            return {
                ...prev,
                productItems: updatedItems,
            };
        });
    };

    const onProductCategoryChange = (newValue) => {
        setProduct((prev) => ({
            ...prev,
            productCategory: newValue,
        }));
    }

    const updateProductItem = (e, index) => {
        e.preventDefault();
        setWaitingForServer(true)
        const item = product.productItems[index]
        productService.updateProductItem(item.id, item)
            .then(() => {
                sendAlertMessage('Termék példány sikeresen módosítva')
            })
            .catch(error => sendAlertMessage(error?.message || 'Termék példány módosítása sikertelen', 'error'))
            .finally(() => {
                setWaitingForServer(false)
                retrieveProduct()
            })
    };

    const create = (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        productService.createProduct(product)
            .then(response => {
                sendAlertMessage('Termék sikeresen létrehozva')
                navigate('/dashboard/storage/products/' + response);
            })
            .catch(error => sendAlertMessage(error?.message || error, 'error'))
            .finally(() => setWaitingForServer(false))
    };

    const update = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(product)], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.patch(API_URL + `/products/${id}`, formData, {headers: headers});
            if (response) {
                sendAlertMessage('Termék sikeresen mentve')
                setFiles([])
                retrieveProduct()
            }
        } catch (error) {
            sendAlertMessage(error?.response?.data?.message || 'Termék mentése sikertelen', 'error')
        }
        setWaitingForServer(false)
    };

    const changeProductImage = (id, fileId) => {
        productService.changeProductImage(id, fileId)
            .then(() => {
                sendAlertMessage('Termék kép sikeresen beállítva')
                retrieveProduct()
            })
            .catch(error => sendAlertMessage(error?.message || error, 'error'))
    }

    const correctAmount = (productItemId, amount) => {
        setWaitingForCorrection(true)
        productService.correctProductItemAmount(productItemId, amount)
            .then(() => {
                retrieveProduct()
                sendAlertMessage('Sikeres mennyiség korrekció')
            })
            .catch(error => setMessage(error))
            .finally(() => setWaitingForCorrection(false))
    }

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const removeDocumentName = (fileId) => {
        productService.deleteFile(id, fileId)
            .then(() => {
                retrieveProduct()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    };

    const updateFileDescription = (id, form) => {
        cloudStorageFileService.update(id, form)
            .then(() => {
                retrieveProduct()
                sendAlertMessage('Fájl leírása sikeresen módosítva!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    }

    return (
        <ProductEditorTemplate
            {...{
                id,
                product,
                message,
                onChange,
                onChangeCheckbox,
                handleFileChange,
                handleRemoveFile,
                removeDocumentName,
                updateFileDescription,
                onChangeSelectItem,
                onChangeProductItem,
                updateProductItem,
                create,
                update,
                correctAmount,
                sendAlertMessage,
                waitingForServer,
                navigate,
                files,
                waitingForCorrection,
                changeProductImage,
                imgUrl,
                loadingImage,
                productCategories,
                onProductCategoryChange
            }}
        />
    );
};

export default ProductEditor;
